import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  Loader2,
  Save,
  Wrench,
  ShieldCheck,
  Clock,
  Phone,
  Power,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

/* Format Date -> "yyyy-MM-ddTHH:mm" (datetime-local input format, LOCAL time) */
const toLocalInput = (isoOrDate) => {
  if (!isoOrDate) return "";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const DEFAULT_MESSAGE =
  "Website RS Bhayangkara Tarakan sedang dalam pemeliharaan untuk peningkatan layanan. Mohon maaf atas ketidaknyamanannya. Kami akan segera kembali.";

const MaintenancePanel = () => {
  const { authAxios, user } = useAuth();
  const isSuperadmin = user && user.role === "superadmin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    message: DEFAULT_MESSAGE,
    estimated_until_local: "",
    contact_phone: "+6282241761118",
  });
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const load = () => {
    setLoading(true);
    authAxios
      .get("/maintenance")
      .then((r) => {
        const d = r.data || {};
        setForm({
          enabled: !!d.enabled,
          message: d.message || DEFAULT_MESSAGE,
          estimated_until_local: toLocalInput(d.estimated_until),
          contact_phone: d.contact_phone || "+6282241761118",
        });
        setLastUpdatedAt(d.updated_at || null);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Gagal memuat status maintenance");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperadmin) load();
  }, [isSuperadmin]);

  const save = async (overrides = {}) => {
    setSaving(true);
    const payload = {
      enabled: overrides.enabled !== undefined ? overrides.enabled : form.enabled,
      message: form.message,
      estimated_until: fromLocalInput(form.estimated_until_local),
      contact_phone: form.contact_phone,
    };
    try {
      const { data } = await authAxios.put("/admin/maintenance", payload);
      setLastUpdatedAt(data.updated_at || new Date().toISOString());
      // Sync toggle state if we did an override (e.g. quick on/off buttons)
      if (overrides.enabled !== undefined) {
        setForm((f) => ({ ...f, enabled: overrides.enabled }));
      }
      toast.success(
        payload.enabled
          ? "Maintenance mode AKTIF — publik tidak dapat mengakses website"
          : "Maintenance mode NONAKTIF — website normal kembali"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div
        data-testid="admin-maintenance-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">
          Maintenance Mode hanya dapat diakses oleh <b>superadmin</b>.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const isActive = form.enabled;

  return (
    <div data-testid="admin-panel-maintenance" className="space-y-6 max-w-3xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="h-6 w-6 text-blue-800" />
            Maintenance Mode
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Aktifkan untuk menutup akses publik ke website sementara. Admin panel
            tetap dapat diakses agar Anda bisa menonaktifkannya kembali.
          </p>
        </div>
        <a
          href="/?admin=1"
          target="_blank"
          rel="noreferrer"
          data-testid="maintenance-preview-link"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-slate-900 text-white hover:bg-slate-800"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Buka Website (bypass admin)
        </a>
      </div>

      {/* Status banner */}
      <div
        data-testid="maintenance-status-banner"
        className={`p-5 rounded-2xl border flex items-start gap-4 ${
          isActive
            ? "bg-red-50 border-red-200"
            : "bg-emerald-50 border-emerald-200"
        }`}
      >
        <div
          className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${
            isActive ? "bg-red-500 text-white" : "bg-emerald-500 text-white"
          }`}
        >
          {isActive ? (
            <AlertTriangle className="h-6 w-6" />
          ) : (
            <Power className="h-6 w-6" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`font-heading font-bold text-lg ${
              isActive ? "text-red-800" : "text-emerald-800"
            }`}
          >
            {isActive ? "Maintenance AKTIF" : "Website Normal"}
          </div>
          <div
            className={`text-sm mt-0.5 ${
              isActive ? "text-red-700" : "text-emerald-700"
            }`}
          >
            {isActive
              ? "Publik saat ini melihat halaman pemeliharaan."
              : "Publik dapat mengakses website secara normal."}
          </div>
          {lastUpdatedAt && (
            <div className="text-xs text-slate-500 mt-1">
              Terakhir diubah:{" "}
              {new Date(lastUpdatedAt).toLocaleString("id-ID")}
            </div>
          )}
        </div>
        {/* Quick toggle */}
        <button
          type="button"
          onClick={() => save({ enabled: !isActive })}
          disabled={saving}
          data-testid="maintenance-quick-toggle"
          className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition ${
            isActive
              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
              : "bg-red-600 hover:bg-red-700 text-white"
          } disabled:opacity-50`}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Power className="h-4 w-4" />
          )}
          {isActive ? "Nonaktifkan" : "Aktifkan Sekarang"}
        </button>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
        {/* Message */}
        <div>
          <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
            Pesan Pemeliharaan
          </label>
          <textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="maintenance-input-message"
            rows={4}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-y"
            placeholder="Tuliskan pesan yang akan tampil kepada pengunjung..."
          />
          <p className="text-xs text-slate-400 mt-1">
            Contoh: &quot;Website sedang dalam pemeliharaan hingga pukul 14:00 WITA.&quot;
          </p>
        </div>

        {/* Estimated end time */}
        <div>
          <label className=" block text-sm font-heading font-semibold text-slate-800 mb-2">
            <Clock className="inline h-4 w-4 mr-1 text-blue-700" />
            Estimasi Selesai (opsional)
          </label>
          <input
            type="datetime-local"
            value={form.estimated_until_local}
            onChange={(e) =>
              setForm({ ...form, estimated_until_local: e.target.value })
            }
            data-testid="maintenance-input-eta"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">
            Jika diisi, halaman publik akan menampilkan <b>hitung mundur live</b>{" "}
            ke waktu ini. Kosongkan jika tidak yakin durasi.
          </p>
        </div>

        {/* Contact phone */}
        <div>
          <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
            <Phone className="inline h-4 w-4 mr-1 text-blue-700" />
            Nomor Kontak Darurat
          </label>
          <input
            type="tel"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            data-testid="maintenance-input-phone"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="+6282241761118"
          />
          <p className="text-xs text-slate-400 mt-1">
            Format internasional. Digunakan untuk tombol WhatsApp & Telepon di
            halaman pemeliharaan.
          </p>
        </div>

        {/* Save configuration only (without toggling) */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => save()}
            disabled={saving}
            data-testid="maintenance-save-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-blue-800 hover:bg-blue-900 text-white shadow-md disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Simpan Konfigurasi
          </button>
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-blue-50 border border-blue-100 text-xs text-blue-900">
        <b>Catatan:</b> Untuk pengujian, buka halaman publik dengan menambah query{" "}
        <code className="px-1 py-0.5 rounded bg-white text-blue-700 font-mono">
          ?admin=1
        </code>{" "}
        (mis. <code>/?admin=1</code>) — Anda akan bypass halaman pemeliharaan.
        Rute <code>/admin/*</code> selalu bebas maintenance.
      </div>
    </div>
  );
};

export default MaintenancePanel;
